import {
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Archive,
  Ban,
  BookMarked,
  BookOpen,
  ChevronDown,
  CircleHelp,
  Copy,
  CopyX,
  Eye,
  FilePenLine,
  Hammer,
  Lock,
  Pencil,
  Plus,
  School,
  Settings,
  Shield,
  Megaphone,
  UserPlus,
  UserRound,
  Users,
  UserX,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { NotificationModal } from "@/components/modals";
import CourseEnrollDialog, { type EnrollTarget } from "@/components/courses/CourseEnrollDialog";
import SchoolProposalList from "@/components/courses/SchoolProposalList";
import CourseAnnounceDialog, { type AnnounceTarget } from "@/components/courses/CourseAnnounceDialog";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import {
  COURSE_DIALOG_STYLE,
  CourseMetaFields,
  taxonomyKindLabel,
  type TaxonomyOption,
} from "@/components/courses/CourseMetaFields";
import { api, del, errorMessage, patch, post } from "@/lib/api";
import { publishCoursePageScopes } from "@/hooks/useCoursePageScopes";
import useFloatRowInView, { floatRowStyle } from "@/hooks/useFloatRowInView";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./Courses.css";

interface Assignment {
  id: string;
  schoolId: string;
  schoolName: string;
  courseId: string;
  canEdit: boolean;
  canCopy?: boolean;
  canPropose?: boolean;
  canEnroll?: boolean;
  withdrawn?: boolean;
  modLock?: boolean;
  grantedAt?: string;
  canBuild?: boolean;
}

interface CourseRow {
  id: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  isMaster?: boolean;
  placement?: "shelf" | "catalog" | "school";
  ownerSchoolId?: string | null;
  sourceCourseId?: string | null;
  canEdit?: boolean;
  canCopy?: boolean;
  canPropose?: boolean;
  modLock?: boolean;
  primaryTaxonomy?: { id: string; title: string | null; type: string | null } | null;
  assignments: Assignment[];
  canBuild?: boolean;
  catalogUpgrade?: { id: string; createdAt?: string; createdByUisUserId?: string | null } | null;
  author?: {
    userId?: string | null;
    name?: string | null;
    schoolId?: string | null;
    schoolName?: string | null;
  } | null;
}

interface SchoolOption {
  id: string;
  name: string;
}

interface CatalogUpgradeRow {
  id: string;
  courseId: string;
  courseTitle?: string;
  createdAt?: string;
  createdByUisUserId?: string | null;
  createdByName?: string | null;
}

function locksFromUi(access: AccessLevel, copyAccess: CopyLevel, modLock: boolean) {
  return {
    canEdit: access === "edit",
    canPropose: access !== "view",
    canCopy: copyAccess === "allow",
    modLock,
  };
}
type AccessLevel = "edit" | "propose" | "view";
type CopyLevel = "allow" | "deny";
type EnrollLevel = "allow" | "deny";
type AccessAudience = "school" | "catalog";
type CoursesPageMode = "catalog" | "school" | "others";
type CoursesLayout = "list" | "grid";
type AssignMode = "pin" | "copy";

function requestedWhen(iso?: string) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const now = new Date();
  const time = then.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (then.toDateString() === now.toDateString()) return `today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (then.toDateString() === yesterday.toDateString()) return `yesterday, ${time}`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function requestedByLine(row: CatalogUpgradeRow) {
  const who = row.createdByName?.trim() || "Someone";
  const when = requestedWhen(row.createdAt);
  return when ? `${who} · ${when}` : who;
}

function staffAssignment(course: CourseRow): Assignment {
  return {
    id: course.id,
    schoolId: "",
    schoolName: "Catalog",
    courseId: course.id,
    canEdit: course.canEdit !== false,
    canCopy: course.canCopy !== false,
    canPropose: Boolean(course.canPropose),
    canEnroll: false,
    withdrawn: false,
    modLock: Boolean(course.modLock),
  };
}

/** Default NotificationModal is 22.5rem × 32rem; this form needs more room. */
const SCHOOL_ACCESS_DIALOG_STYLE: CSSProperties = {
  width: "min(32rem, 96vw)",
  maxHeight: "min(90vh, 52rem)",
  padding: "1.35rem 1.5rem 1.2rem",
};

const SCOPE_STORAGE_KEY = "al-courses-scope-id";
const CATALOG_SCOPE_ID = "catalog";
/** Courses this account authored — shelf, catalog, and school copies. */
const MY_COURSES_SCOPE_ID = "mine";
/** Other people's unowned non-templates. Sibling of My courses. */
const OTHERS_SCOPE_ID = "others";
const SCOPE_SWITCH_LABEL = "Catalog, your courses, others' courses, or schools in your scope";

type MineSectionKey = "shelf" | "catalog" | "school" | "assigned";

const MINE_SECTIONS: { key: MineSectionKey; title: string; subtitle: string }[] = [
  {
    key: "shelf",
    title: "My shelf",
    subtitle: "Not in the catalog and not on a school yet.",
  },
  {
    key: "catalog",
    title: "Also in catalog",
    subtitle: "Templates you created. Giving one to a school leaves this row here.",
  },
  {
    key: "school",
    title: "On a school",
    subtitle: "This course itself is on a school — no extra copy.",
  },
  {
    key: "assigned",
    title: "Assigned to schools",
    subtitle: "Copies you gave a school. The original stays in the catalog or on your shelf.",
  },
];

const NAV_MINE_SECTIONS: { key: MineSectionKey; name: string }[] = [
  { key: "shelf", name: "My shelf" },
  { key: "catalog", name: "Also in catalog" },
  { key: "school", name: "On a school" },
  { key: "assigned", name: "Assigned to schools" },
];

const MINE_SECTION_ICONS: Record<MineSectionKey, LucideIcon> = {
  shelf: BookMarked,
  catalog: BookOpen,
  school: School,
  assigned: Copy,
};

function coursePlacement(course: CourseRow): "shelf" | "catalog" | "school" {
  return course.placement === "catalog" || course.placement === "school" ? course.placement : "shelf";
}

/** Give-out / in-school copy: owned and pointed at a source. Pin has owner, no source. */
function mineSectionKey(course: CourseRow): MineSectionKey {
  if (course.ownerSchoolId || course.placement === "school") {
    return course.sourceCourseId ? "assigned" : "school";
  }
  return course.placement === "catalog" ? "catalog" : "shelf";
}

function creatorSchoolOption(course: CourseRow | null, schools: SchoolOption[]) {
  const id = course?.author?.schoolId;
  if (!id) return null;
  return schools.find((row) => String(row.id) === String(id)) || null;
}

function visibleMineNavSections(courses: CourseRow[]) {
  return NAV_MINE_SECTIONS.filter((section) =>
    courses.some((course) => mineSectionKey(course) === section.key),
  );
}

function othersAuthorGroups(courses: CourseRow[]) {
  const map = new Map<string, { key: string; title: string; subtitle: string | null; items: CourseRow[] }>();
  for (const course of courses) {
    const author = course.author;
    const userId = author?.userId || "unknown";
    const schoolId = author?.schoolId || "platform";
    const key = `${userId}::${schoolId}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(course);
      continue;
    }
    map.set(key, {
      key,
      title: author?.name || "Someone",
      subtitle: author?.schoolName || null,
      items: [course],
    });
  }
  return [...map.values()].sort((a, b) => {
    const as = a.subtitle === "Platform" || !a.subtitle ? "\uFFFF" : a.subtitle;
    const bs = b.subtitle === "Platform" || !b.subtitle ? "\uFFFF" : b.subtitle;
    const school = as.localeCompare(bs);
    if (school) return school;
    return a.title.localeCompare(b.title);
  });
}

function readStoredScopeId() {
  try {
    return sessionStorage.getItem(SCOPE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function storeScopeId(id: string) {
  try {
    if (id) sessionStorage.setItem(SCOPE_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function assignmentAccess(assignment: Assignment): AccessLevel {
  return assignment.canEdit ? "edit" : assignment.canPropose ? "propose" : "view";
}

function assignmentCopy(assignment: Assignment): CopyLevel {
  return assignment.canCopy === false ? "deny" : "allow";
}

function assignmentEnroll(assignment: Assignment): EnrollLevel {
  return assignment.canEnroll === false ? "deny" : "allow";
}

function assignmentAcceptsEnroll(assignment: Assignment) {
  return !assignment.withdrawn && assignment.canEnroll !== false;
}

function SettingRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="al-courses-setting">
      <div className="al-courses-setting-copy">
        <span className="al-courses-label">{label}</span>
        {hint ? <p className="al-courses-setting-hint">{hint}</p> : null}
      </div>
      <div className="al-courses-seg" role="presentation">
        {children}
      </div>
    </div>
  );
}

function SegOption({
  name,
  value,
  checked,
  disabled,
  onChange,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <label className={`al-courses-seg-opt${checked ? " is-on" : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span>{children}</span>
    </label>
  );
}

function CourseAccessFields({
  accessName,
  copyName,
  access,
  copyAccess,
  onAccess,
  onCopy,
  disabled = false,
  locksDisabled,
  lockName,
  modLock,
  onModLock,
  enrollName,
  enrollAccess,
  onEnroll,
  showLocks = true,
  audience = "school",
}: {
  accessName: string;
  copyName: string;
  access: AccessLevel;
  copyAccess: CopyLevel;
  onAccess: (value: AccessLevel) => void;
  onCopy: (value: CopyLevel) => void;
  disabled?: boolean;
  /** Access / Copy / Mod lock only. Enrollment still follows `disabled`. */
  locksDisabled?: boolean;
  lockName?: string;
  modLock?: boolean;
  onModLock?: (value: boolean) => void;
  enrollName?: string;
  enrollAccess?: EnrollLevel;
  onEnroll?: (value: EnrollLevel) => void;
  showLocks?: boolean;
  audience?: AccessAudience;
}) {
  const catalog = audience === "catalog";
  const lockOff = Boolean(disabled || locksDisabled);
  const accessHint =
    access === "propose"
      ? catalog
        ? "Other catalog editors cannot change the live template. They can submit proposals for you to review."
        : "The school cannot change the live copy. They can submit proposals for you to review."
      : access === "edit"
        ? catalog
          ? "Other catalog editors can change this template."
          : "The school can change their copy. The original stays as it is."
        : catalog
          ? "Other catalog editors cannot change this template or propose."
          : "The school has the course but cannot change it or propose.";
  const enrollHint =
    enrollAccess === "allow"
      ? "People can be added to this school’s copy. Existing enrollments are unchanged."
      : "The course stays in the catalog, but nobody new can be enrolled until this is turned back on.";
  const copyHint =
    copyAccess === "allow"
      ? catalog
        ? "Other catalog editors with Copy Courses can duplicate this template."
        : "The school can duplicate this course. The duplicate keeps these access, copy, and enrollment settings."
      : catalog
        ? "Other catalog editors cannot duplicate this template."
        : "The school cannot duplicate this course.";
  const lockHint = modLock
    ? catalog
      ? "Hard freeze. Other catalog editors cannot edit or propose in Course Builder. Only Edit Despite Mod Lock can write through."
      : "Hard freeze. Only Edit Despite Mod Lock can write through."
    : catalog
      ? "Edit and propose in Course Builder follow Access above for other catalog editors."
      : "Edit and propose follow the access choice above.";

  return (
    <>
      {showLocks ? (
        <SettingRow label="Access" hint={accessHint}>
          <SegOption
            name={accessName}
            value="propose"
            checked={access === "propose"}
            disabled={lockOff}
            onChange={() => onAccess("propose")}
          >
            Propose
          </SegOption>
          <SegOption
            name={accessName}
            value="edit"
            checked={access === "edit"}
            disabled={lockOff}
            onChange={() => onAccess("edit")}
          >
            Edit
          </SegOption>
          <SegOption
            name={accessName}
            value="view"
            checked={access === "view"}
            disabled={lockOff}
            onChange={() => onAccess("view")}
          >
            View only
          </SegOption>
        </SettingRow>
      ) : null}

      {onEnroll && enrollName && enrollAccess ? (
        <SettingRow label="Enrollment" hint={enrollHint}>
          <SegOption
            name={enrollName}
            value="allow"
            checked={enrollAccess === "allow"}
            disabled={disabled}
            onChange={() => onEnroll("allow")}
          >
            Can enroll
          </SegOption>
          <SegOption
            name={enrollName}
            value="deny"
            checked={enrollAccess === "deny"}
            disabled={disabled}
            onChange={() => onEnroll("deny")}
          >
            Cannot enroll
          </SegOption>
        </SettingRow>
      ) : null}

      {showLocks ? (
        <SettingRow label="Copy" hint={copyHint}>
          <SegOption
            name={copyName}
            value="allow"
            checked={copyAccess === "allow"}
            disabled={lockOff}
            onChange={() => onCopy("allow")}
          >
            Can copy
          </SegOption>
          <SegOption
            name={copyName}
            value="deny"
            checked={copyAccess === "deny"}
            disabled={lockOff}
            onChange={() => onCopy("deny")}
          >
            Cannot copy
          </SegOption>
        </SettingRow>
      ) : null}

      {showLocks && onModLock && lockName ? (
        <SettingRow label="Mod lock" hint={lockHint}>
          <SegOption
            name={lockName}
            value="off"
            checked={!modLock}
            disabled={lockOff}
            onChange={() => onModLock(false)}
          >
            Unlocked
          </SegOption>
          <SegOption
            name={lockName}
            value="on"
            checked={Boolean(modLock)}
            disabled={lockOff}
            onChange={() => onModLock(true)}
          >
            Mod locked
          </SegOption>
        </SettingRow>
      ) : null}
    </>
  );
}

function assignmentAccessMeta(assignment: Assignment) {
  if (assignment.withdrawn) {
    return { Icon: Archive, label: "Withdrawn", tone: "warn" as const };
  }
  if (assignment.modLock) {
    return { Icon: Lock, label: "Mod locked", tone: "off" as const };
  }
  if (assignment.canEdit) {
    return { Icon: Pencil, label: "Can edit", tone: "on" as const };
  }
  if (assignment.canPropose) {
    return { Icon: FilePenLine, label: "Can propose", tone: "on" as const };
  }
  return { Icon: Eye, label: "View only", tone: "off" as const };
}

function AssignmentFlags({
  assignment,
  courseTitle,
  showOpenCopy = false,
  showEnroll = false,
  busy = false,
  onPermissions,
  onEnroll,
}: {
  assignment: Assignment;
  courseTitle?: string;
  showOpenCopy?: boolean;
  showEnroll?: boolean;
  busy?: boolean;
  onPermissions?: () => void;
  onEnroll?: () => void;
}) {
  const access = assignmentAccessMeta(assignment);
  const canCopy = assignment.canCopy !== false;
  const enrollOpen = assignmentAcceptsEnroll(assignment);
  const AccessIcon = access.Icon;

  return (
    <span className="al-courses-flags">
      <span className={`al-courses-flag al-courses-flag--${access.tone}`} title={access.label}>
        <AccessIcon size={13} aria-hidden="true" />
        <span className="al-courses-sr">{access.label}</span>
      </span>
      <span
        className={`al-courses-flag${canCopy ? " al-courses-flag--on" : " al-courses-flag--off"}`}
        title={canCopy ? "Can copy" : "Cannot copy"}
      >
        {canCopy ? <Copy size={13} aria-hidden="true" /> : <CopyX size={13} aria-hidden="true" />}
        <span className="al-courses-sr">{canCopy ? "Can copy" : "Cannot copy"}</span>
      </span>
      {assignment.withdrawn ? null : enrollOpen ? (
        showEnroll && onEnroll ? (
          <button
            type="button"
            className="al-courses-flag-btn"
            title={`Enroll people at ${assignment.schoolName}`}
            aria-label={`Enroll people in ${courseTitle || assignment.schoolName}`}
            disabled={busy}
            onClick={onEnroll}
          >
            <UserPlus size={13} aria-hidden="true" />
          </button>
        ) : (
          <span className="al-courses-flag al-courses-flag--on" title="Can enroll">
            <Users size={13} aria-hidden="true" />
            <span className="al-courses-sr">Can enroll</span>
          </span>
        )
      ) : (
        <span className="al-courses-flag al-courses-flag--off" title="Cannot enroll">
          <UserX size={13} aria-hidden="true" />
          <span className="al-courses-sr">Cannot enroll</span>
        </span>
      )}
      {showOpenCopy ? (
        <Link
          to={`/courses/${encodeURIComponent(assignment.courseId)}/build`}
          className="al-courses-flag-btn"
          title={`Open ${assignment.schoolName}’s copy`}
          aria-label={`Open ${assignment.schoolName}’s copy`}
        >
          <Hammer size={13} aria-hidden="true" />
        </Link>
      ) : null}
      {onPermissions ? (
        <button
          type="button"
          className="al-courses-flag-btn"
          title={assignment.schoolName === "Catalog" ? "Change catalog access" : "Change school access"}
          aria-label={
            assignment.schoolName === "Catalog"
              ? `Change catalog access for ${courseTitle}`
              : `Change access for ${assignment.schoolName}`
          }
          disabled={busy}
          onClick={onPermissions}
        >
          <Settings size={13} aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

function CatalogRequestText({
  pending,
  busy = false,
  onRequest,
  onWithdraw,
}: {
  pending?: { id: string } | null;
  busy?: boolean;
  onRequest?: () => void;
  onWithdraw?: () => void;
}) {
  if (pending) {
    return (
      <>
        <span className="al-courses-upgrade-pending">Catalog requested</span>
        {onWithdraw ? (
          <button
            type="button"
            className="al-courses-catalog-remove"
            disabled={busy}
            onClick={onWithdraw}
          >
            Withdraw request
          </button>
        ) : null}
      </>
    );
  }
  if (!onRequest) return null;
  return (
    <button type="button" className="al-courses-catalog-remove" disabled={busy} onClick={onRequest}>
      Request catalog
    </button>
  );
}

export default function Courses() {
  const { capabilities, pageAccess, loading: authLoading, session } = useAuth();
  const { toast, notice } = useNotification();

  const canAssign = Boolean(capabilities?.assignCoursesToSchools);
  const canPlaceOnSchool = Boolean(capabilities?.placeOnSchool);
  const canCopyCourses = Boolean(capabilities?.copyCourses);
  const canSetCopyAccess = Boolean(capabilities?.setSchoolCopyEdit);
  const canEditDespiteModLock = Boolean(capabilities?.editDespiteModLock);
  const canSeeCatalog = Boolean(
    capabilities?.viewMasterCatalog ||
      capabilities?.createMasterCourses ||
      capabilities?.editMasterCourses ||
      capabilities?.proposeMasterCourses ||
      capabilities?.reviewCatalogProposals ||
      capabilities?.updateCatalogAccess ||
      capabilities?.removeMasterCourses,
  );
  const canCreate = Boolean(capabilities?.createCourses || capabilities?.createMasterCourses);
  const canCreateMaster = Boolean(capabilities?.createMasterCourses);
  const canProposeToCatalog = Boolean(capabilities?.proposeToCatalog);
  const canProposeAssignToSchool = Boolean(capabilities?.proposeAssignToSchool);
  const canUpdateCatalogAccess = Boolean(capabilities?.updateCatalogAccess);
  const canCreateRegular = Boolean(capabilities?.createCourses);
  const canEdit = Boolean(capabilities?.editCourses);
  const canEditMaster = Boolean(capabilities?.editMasterCourses);
  const canProposeMaster = Boolean(capabilities?.proposeMasterCourses);
  const canSetMasterAccess = Boolean(canUpdateCatalogAccess || canEditDespiteModLock);
  const canView = Boolean(capabilities?.viewCourses);
  const canOpen = Boolean(
    pageAccess?.courses ||
      canView ||
      canAssign ||
      canPlaceOnSchool ||
      canCopyCourses ||
      canSetCopyAccess ||
      canCreate ||
      canProposeToCatalog ||
      canProposeAssignToSchool ||
      canUpdateCatalogAccess ||
      capabilities?.viewMasterCatalog ||
      capabilities?.editMasterCourses ||
      capabilities?.proposeMasterCourses ||
      capabilities?.reviewCatalogProposals ||
      capabilities?.reviewProposals ||
      capabilities?.viewOthersCourses ||
      capabilities?.editCourses ||
      capabilities?.removeCourses ||
      capabilities?.removeMasterCourses,
  );
  const canOpenBuilder = Boolean(pageAccess?.["course-builder"]);
  const canManageEnrollments = Boolean(capabilities?.manageEnrollments && capabilities?.viewUsers);
  const canPostAnnouncements = Boolean(capabilities?.postAnnouncements);
  const canRequestCatalogFor = (course: CourseRow) =>
    Boolean(canProposeToCatalog && !canCreateMaster && !course.isMaster && !course.catalogUpgrade);

  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [pageMode, setPageMode] = useState<CoursesPageMode>("catalog");
  const [scopedSchools, setScopedSchools] = useState<SchoolOption[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [schoolMenuOpen, setSchoolMenuOpen] = useState(false);
  const schoolMenuRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const courseListRef = useRef<HTMLDivElement | null>(null);
  const createSlotRef = useRef<HTMLDivElement | null>(null);
  const [mineNavSection, setMineNavSection] = useState<string | null>(null);
  const pendingMineSection = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAssignment, setBusyAssignment] = useState<string | null>(null);
  const [brokenCovers, setBrokenCovers] = useState<Record<string, boolean>>({});

  // Popups are keyed by course/assignment id so they stay live across a reload.
  const [assignForId, setAssignForId] = useState<string | null>(null);
  const [permissionsForId, setPermissionsForId] = useState<string | null>(null);
  const [expandedCourseIds, setExpandedCourseIds] = useState<Record<string, boolean>>({});
  const [schoolIds, setSchoolIds] = useState<string[]>([]);
  const [assignMode, setAssignMode] = useState<AssignMode>("copy");
  const [access, setAccess] = useState<AccessLevel>("propose");
  const [copyAccess, setCopyAccess] = useState<CopyLevel>("deny");
  const [enrollAccess, setEnrollAccess] = useState<EnrollLevel>("allow");
  const [permModLock, setPermModLock] = useState(false);
  const [withdrawTarget, setWithdrawTarget] = useState<{ course: CourseRow; assignment: Assignment } | null>(null);
  const [masterTarget, setMasterTarget] = useState<CourseRow | null>(null);
  const [masterAccess, setMasterAccess] = useState<AccessLevel>("edit");
  const [masterCopy, setMasterCopy] = useState<CopyLevel>("deny");
  const [masterModLock, setMasterModLock] = useState(false);
  const [createAccess, setCreateAccess] = useState<AccessLevel>("edit");
  const [createCopy, setCreateCopy] = useState<CopyLevel>("deny");
  const [createModLock, setCreateModLock] = useState(false);
  const [catalogUpgrades, setCatalogUpgrades] = useState<CatalogUpgradeRow[]>([]);
  const [showCatalogInbox, setShowCatalogInbox] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editForId, setEditForId] = useState<string | null>(null);
  const [showCourseHelp, setShowCourseHelp] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [asMaster, setAsMaster] = useState(false);
  const [showMasterInfo, setShowMasterInfo] = useState(false);
  const [taxonomyId, setTaxonomyId] = useState("");
  const [taxonomyOptions, setTaxonomyOptions] = useState<TaxonomyOption[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [enrollTarget, setEnrollTarget] = useState<EnrollTarget | null>(null);
  const [announceTarget, setAnnounceTarget] = useState<AnnounceTarget | null>(null);
  const [taxonomyPickerOpen, setTaxonomyPickerOpen] = useState(false);
  // List/grid switcher is hidden for now; list is the only layout.
  const layout: CoursesLayout = "list";
  const showCreateGhost =
    pageMode === "catalog" &&
    selectedSchoolId !== OTHERS_SCOPE_ID &&
    canCreate &&
    !showCreate &&
    !loading;
  const pinCreate = useFloatRowInView(
    courseListRef,
    createSlotRef,
    showCreateGhost,
    `${courses.length}-${selectedSchoolId}`,
  );

  const assignCourse = courses.find((c) => c.id === assignForId) ?? null;
  const editCourse = courses.find((c) => c.id === editForId) ?? null;
  const permissionsContext = (() => {
    if (!permissionsForId) return null;
    for (const course of courses) {
      if (
        course.isMaster &&
        !course.ownerSchoolId &&
        course.id === permissionsForId
      ) {
        return { course, assignment: staffAssignment(course), kind: "catalog" as const };
      }
      const assignment = course.assignments.find((row) => row.id === permissionsForId);
      if (assignment) return { course, assignment, kind: "school" as const };
    }
    return null;
  })();

  const showError = useCallback(
    (title: string, err: unknown) => {
      notice({ type: "error", title, subtext: errorMessage(err), duration: 7000 });
    },
    [notice],
  );

  const load = useCallback(async (schoolId?: string) => {
    setLoading(true);
    try {
      const query = schoolId ? `?schoolId=${encodeURIComponent(schoolId)}` : "";
      const envelope = await api<{
        success: boolean;
        mode?: CoursesPageMode;
        schools?: SchoolOption[];
        selectedSchoolId?: string | null;
        catalogUpgrades?: CatalogUpgradeRow[];
        data: CourseRow[];
      }>(`/api/courses${query}`);
      const mode =
        envelope.mode === "school" ? "school" : envelope.mode === "others" ? "others" : "catalog";
      setPageMode(mode);
      setScopedSchools(Array.isArray(envelope.schools) ? envelope.schools : []);
      const selected = envelope.selectedSchoolId || "";
      setSelectedSchoolId(selected);
      if (selected) storeScopeId(selected);
      setCourses(Array.isArray(envelope.data) ? envelope.data : []);
      setCatalogUpgrades(Array.isArray(envelope.catalogUpgrades) ? envelope.catalogUpgrades : []);
    } catch (err) {
      showError("Could not load courses", err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadTaxonomy = useCallback(async () => {
    if (!canCreate && !canEdit) return;
    setTaxonomyLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: TaxonomyOption[] }>("/api/courses/taxonomy");
      setTaxonomyOptions(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      showError("Could not load taxonomy", err);
      setTaxonomyOptions([]);
    } finally {
      setTaxonomyLoading(false);
    }
  }, [canCreate, canEdit, showError]);

  const loadSchools = useCallback(async () => {
    if (!canAssign && !canPlaceOnSchool) return;
    try {
      const envelope = await api<{ success: boolean; data: SchoolOption[] }>(
        "/api/courses/available-schools",
      );
      setSchools(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      showError("Could not load schools", err);
    }
  }, [canAssign, canPlaceOnSchool, showError]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load(readStoredScopeId() || undefined);
    void loadSchools();
  }, [authLoading, canOpen, load, loadSchools]);

  useEffect(() => {
    if (!schoolMenuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (schoolMenuRef.current && !schoolMenuRef.current.contains(event.target as Node)) {
        setSchoolMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSchoolMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [schoolMenuOpen]);

  const selectedScopeIdRef = useRef(selectedSchoolId);
  selectedScopeIdRef.current = selectedSchoolId;

  const scrollListToTop = useCallback(() => {
    const scroller = pageRef.current?.closest(".al-shell-body");
    if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, []);

  const scrollToMineSection = useCallback((key: string) => {
    const heading = pageRef.current?.querySelector(`#al-mine-${key}`);
    const scroller = pageRef.current?.closest(".al-shell-body");
    if (!(heading instanceof HTMLElement) || !(scroller instanceof HTMLElement)) {
      scrollListToTop();
      return;
    }
    const top =
      heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTop = Math.max(0, top - 8);
  }, [scrollListToTop]);

  const pickScope = useCallback((id: string) => {
    setSchoolMenuOpen(false);
    if (id === selectedScopeIdRef.current) {
      if (id === MY_COURSES_SCOPE_ID) {
        pendingMineSection.current = null;
        setMineNavSection(null);
        scrollListToTop();
      }
      return;
    }
    pendingMineSection.current = null;
    setMineNavSection(null);
    storeScopeId(id);
    setSelectedSchoolId(id);
    scrollListToTop();
    void load(id);
  }, [load, scrollListToTop]);

  const pickMineSection = useCallback((key: string | null) => {
    if (selectedScopeIdRef.current !== MY_COURSES_SCOPE_ID) {
      pendingMineSection.current = key;
      pickScope(MY_COURSES_SCOPE_ID);
      return;
    }
    setMineNavSection(key);
    if (!key) {
      pendingMineSection.current = null;
      scrollListToTop();
      return;
    }
    pendingMineSection.current = null;
    requestAnimationFrame(() => scrollToMineSection(key));
  }, [pickScope, scrollListToTop, scrollToMineSection]);

  const openCatalogInbox = useCallback(() => setShowCatalogInbox(true), []);

  useEffect(() => {
    if (selectedSchoolId !== MY_COURSES_SCOPE_ID || loading) return;
    const key = pendingMineSection.current;
    if (!key) return;
    pendingMineSection.current = null;
    setMineNavSection(key);
    requestAnimationFrame(() => scrollToMineSection(key));
  }, [selectedSchoolId, loading, courses, scrollToMineSection]);

  useEffect(() => {
    const mineOpen = selectedSchoolId === MY_COURSES_SCOPE_ID;
    const mineSections = mineOpen && !loading ? visibleMineNavSections(courses) : [];
    const catalogInbox =
      canCreateMaster && catalogUpgrades.length > 0
        ? { count: catalogUpgrades.length, open: openCatalogInbox }
        : undefined;
    const hasSubmenu = scopedSchools.length > 1 || mineSections.length > 0 || Boolean(catalogInbox);
    if (authLoading || !canOpen || !hasSubmenu) {
      publishCoursePageScopes(null);
      return () => publishCoursePageScopes(null);
    }
    publishCoursePageScopes({
      scopes: scopedSchools,
      selectedId: selectedSchoolId,
      pick: pickScope,
      mineSections: mineSections.length ? mineSections : undefined,
      mineSectionId: mineOpen ? mineNavSection : null,
      pickMineSection: mineOpen ? pickMineSection : undefined,
      catalogInbox,
    });
    return () => publishCoursePageScopes(null);
  }, [
    authLoading,
    canOpen,
    canCreateMaster,
    catalogUpgrades.length,
    openCatalogInbox,
    scopedSchools,
    selectedSchoolId,
    pickScope,
    pickMineSection,
    mineNavSection,
    courses,
    loading,
  ]);

  if (authLoading) {
    return <LoadingOverlay isVisible message="Loading…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("courses")} />;
  }

  const openAssign = (courseId: string) => {
    const course = courses.find((row) => row.id === courseId) ?? null;
    const placing = Boolean(course && !course.isMaster);
    setAssignForId(courseId);
    setAssignMode(placing ? "pin" : "copy");
    setAccess("propose");
    setCopyAccess("deny");
    setEnrollAccess("allow");
    const taken = new Set((course?.assignments ?? []).map((row) => String(row.schoolId)));
    const options = schools.filter((row) => !taken.has(String(row.id)));
    const creatorSchool = placing ? creatorSchoolOption(course, options) : null;
    const globalAssigner = session?.acuityLearn?.schoolScope?.type === "global";
    const home = String(session?.acuityLearn?.resolvedSchool?.groupId || "");
    const defaultSchool = creatorSchool
      ? String(creatorSchool.id)
      : globalAssigner
        ? ""
        : options.some((row) => String(row.id) === home)
          ? home
          : options.length === 1
            ? String(options[0].id)
            : "";
    setSchoolIds(defaultSchool ? [defaultSchool] : []);
  };

  const closeAssign = () => {
    setAssignForId(null);
    setSchoolIds([]);
    setAssignMode("copy");
    setAccess("propose");
    setCopyAccess("deny");
    setEnrollAccess("allow");
  };

  const openPermissions = (assignment: Assignment) => {
    setPermissionsForId(assignment.id);
    setAccess(assignmentAccess(assignment));
    setCopyAccess(assignmentCopy(assignment));
    setEnrollAccess(assignmentEnroll(assignment));
    setPermModLock(Boolean(assignment.modLock));
  };

  const closePermissions = () => {
    setPermissionsForId(null);
    setWithdrawTarget(null);
  };

  const openCreate = () => {
    setNewTitle("");
    setNewDescription("");
    setAsMaster(canCreateMaster);
    setCreateAccess("edit");
    setCreateCopy("deny");
    setCreateModLock(false);
    setTaxonomyId("");
    setEditForId(null);
    setShowCreate(true);
    void loadTaxonomy();
    const onCatalogPage =
      pageMode === "catalog" &&
      selectedSchoolId !== MY_COURSES_SCOPE_ID &&
      selectedSchoolId !== OTHERS_SCOPE_ID;
    if (canCreateMaster && onCatalogPage) {
      notice({
        type: "warning",
        title: "In Catalog by default",
        subtext:
          "Click the yellow badge to create this in My courses instead of adding it to the catalog right away.",
        duration: 5000,
      });
    }
  };

  const closeCreate = () => {
    setShowCreate(false);
    setShowMasterInfo(false);
  };

  const openEdit = (course: CourseRow) => {
    setShowCreate(false);
    setEditForId(course.id);
    setNewTitle(course.title);
    setNewDescription(course.description || "");
    setTaxonomyId(course.primaryTaxonomy?.id || "");
    void loadTaxonomy();
  };

  const closeEdit = () => {
    setEditForId(null);
  };

  const handleEdit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editCourse || !newTitle.trim()) return;
    setSaving(true);
    try {
      await patch(`/api/courses/${encodeURIComponent(editCourse.id)}`, {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        primaryTaxonomy: taxonomyId || null,
      });
      toast.success("Course updated");
      closeEdit();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not update the course", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await post("/api/courses", {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        isMaster: canCreateMaster ? asMaster : false,
        ...(canCreateMaster && asMaster ? locksFromUi(createAccess, createCopy, createModLock) : {}),
        primaryTaxonomy: taxonomyId || null,
      });
      toast.success(
        canCreateMaster && asMaster ? "Added to catalog" : "Course created — see My courses",
      );
      closeCreate();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not create the course", err);
    } finally {
      setSaving(false);
    }
  };

  const lastCopiedToggle = useRef<string | null>(null);
  const toggleCopiedSchools = (courseId: string) => {
    lastCopiedToggle.current = courseId;
    setExpandedCourseIds((current) => ({ ...current, [courseId]: !current[courseId] }));
  };

  useLayoutEffect(() => {
    const courseId = lastCopiedToggle.current;
    if (!courseId || !expandedCourseIds[courseId]) return;
    const rows = pageRef.current?.querySelector(
      `[data-course-copies="${CSS.escape(courseId)}"]`,
    );
    if (rows instanceof HTMLElement) {
      rows.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [expandedCourseIds]);

  const handleAssign = async (event: FormEvent) => {
    event.preventDefault();
    if (!assignCourse || !schoolIds.length) return;
    const pinThis = !assignCourse.isMaster && assignMode === "pin";
    const targets = pinThis ? schoolIds.slice(0, 1) : schoolIds;
    setSaving(true);
    let given = 0;
    try {
      for (const id of targets) {
        await post(`/api/courses/${encodeURIComponent(assignCourse.id)}/assign`, {
          schoolId: id,
          mode: assignCourse.isMaster ? "copy" : assignMode,
          canEdit: access === "edit",
          canPropose: access === "propose",
          canCopy: copyAccess === "allow",
          canEnroll: enrollAccess === "allow",
        });
        given += 1;
      }
      toast.success(
        pinThis
          ? "Course is on the school"
          : targets.length > 1
            ? `Course given to ${targets.length} schools`
            : access === "edit"
              ? "Course given — school can edit"
              : access === "propose"
                ? "Course given — school can propose"
                : "Course given — view only",
      );
      closeAssign();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      if (given) {
        showError(
          given === 1
            ? "Given to 1 school, then the next one failed"
            : `Given to ${given} schools, then the next one failed`,
          err,
        );
        closeAssign();
        await load(selectedSchoolId || undefined);
      } else {
        showError("Could not give the course", err);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSetAccess = async (
    assignment: Assignment,
    fields: {
      canEdit?: boolean;
      canCopy?: boolean;
      canPropose?: boolean;
      canEnroll?: boolean;
      modLock?: boolean;
    },
  ) => {
    const nextEdit = fields.canEdit ?? assignment.canEdit;
    const nextCopy = fields.canCopy ?? Boolean(assignment.canCopy);
    const nextPropose = fields.canPropose ?? Boolean(assignment.canPropose);
    const nextEnroll = fields.canEnroll ?? assignment.canEnroll !== false;
    const nextLock = fields.modLock ?? Boolean(assignment.modLock);
    if (
      assignment.canEdit === nextEdit &&
      Boolean(assignment.canCopy) === nextCopy &&
      Boolean(assignment.canPropose) === nextPropose &&
      (assignment.canEnroll !== false) === nextEnroll &&
      Boolean(assignment.modLock) === nextLock
    ) {
      closePermissions();
      return;
    }
    setBusyAssignment(assignment.id);
    try {
      await patch(`/api/courses/assignments/${encodeURIComponent(assignment.id)}`, fields);
      toast.success(assignment.schoolName === "Catalog" ? "Catalog access updated" : "School access updated");
      closePermissions();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not change access", err);
    } finally {
      setBusyAssignment(null);
    }
  };

  const handleSavePermissions = async (event: FormEvent) => {
    event.preventDefault();
    if (!permissionsContext) return;
    const catalog = permissionsContext.kind === "catalog";
    const fields = catalog
      ? {
          canEdit: access === "edit",
          canPropose: access === "propose",
          canCopy: copyAccess === "allow",
          modLock: permModLock,
        }
      : canSetCopyAccess
        ? {
            canEdit: access === "edit",
            canPropose: access === "propose",
            canCopy: copyAccess === "allow",
            canEnroll: enrollAccess === "allow",
            modLock: permModLock,
          }
        : { canEnroll: enrollAccess === "allow" };
    await handleSetAccess(permissionsContext.assignment, fields);
  };

  const handleCopyCourse = async (course: CourseRow) => {
    const copyId = course.assignments[0]?.courseId || course.id;
    setBusyAssignment(copyId);
    try {
      await post(`/api/courses/${encodeURIComponent(copyId)}/copy`, {});
      toast.success("Course copied");
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not copy the course", err);
    } finally {
      setBusyAssignment(null);
    }
  };

  const openMasterTarget = (course: CourseRow) => {
    setMasterAccess("edit");
    setMasterCopy("deny");
    setMasterModLock(false);
    setMasterTarget(course);
  };

  const handleSetMaster = async () => {
    if (!masterTarget) return;
    const toCatalog = !masterTarget.isMaster;
    const requestOnly = toCatalog && !canCreateMaster && canProposeToCatalog;
    setBusyAssignment(masterTarget.id);
    try {
      const locks = canCreateMaster && toCatalog ? locksFromUi(masterAccess, masterCopy, masterModLock) : {};
      if (requestOnly) {
        await post(`/api/courses/${encodeURIComponent(masterTarget.id)}/catalog-upgrade`, {});
        toast.success("Catalog upgrade requested");
      } else {
        await patch(`/api/courses/${encodeURIComponent(masterTarget.id)}/master`, {
          isMaster: toCatalog,
          ...locks,
        });
        toast.success(toCatalog ? "Added to catalog" : "Moved to shelf");
      }
      setMasterTarget(null);
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError(
        requestOnly
          ? "Could not request a catalog upgrade"
          : toCatalog
            ? "Could not add the course to the catalog"
            : "Could not move the course to the shelf",
        err,
      );
    } finally {
      setBusyAssignment(null);
    }
  };

  const handleCatalogInbox = async (id: string, action: "apply" | "reject") => {
    setBusyAssignment(id);
    try {
      await post(`/api/courses/catalog-upgrades/${encodeURIComponent(id)}/${action}`, {});
      toast.success(action === "apply" ? "Promoted to catalog" : "Request declined");
      if (catalogUpgrades.length <= 1) setShowCatalogInbox(false);
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError(
        action === "apply" ? "Could not promote the course to the catalog" : "Could not decline the request",
        err,
      );
    } finally {
      setBusyAssignment(null);
    }
  };

  const handleWithdrawUpgrade = async (proposalId: string) => {
    setBusyAssignment(proposalId);
    try {
      await post(`/api/courses/catalog-upgrades/${encodeURIComponent(proposalId)}/withdraw`, {});
      toast.success("Catalog request withdrawn");
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not withdraw the request", err);
    } finally {
      setBusyAssignment(null);
    }
  };

  const handleRevoke = async () => {
    if (!withdrawTarget) return;
    const { course, assignment } = withdrawTarget;
    setBusyAssignment(assignment.id);
    try {
      await del(`/api/courses/assignments/${encodeURIComponent(assignment.id)}`);
      toast.success(`Withdrawn from ${assignment.schoolName}`);
      setWithdrawTarget(null);
      closePermissions();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not withdraw the course", err);
    } finally {
      setBusyAssignment(null);
    }
  };

  const handleRestore = async (assignment: Assignment) => {
    setBusyAssignment(assignment.id);
    try {
      await post(`/api/courses/assignments/${encodeURIComponent(assignment.id)}/restore`, {});
      toast.success(`Restored to ${assignment.schoolName}`);
      closePermissions();
      await load(selectedSchoolId || undefined);
    } catch (err) {
      showError("Could not restore the course", err);
    } finally {
      setBusyAssignment(null);
    }
  };

  const assignedIds = new Set((assignCourse?.assignments ?? []).map((a) => String(a.schoolId)));
  const selectableSchools = schools.filter((s) => !assignedIds.has(String(s.id)));
  const placingDraft = Boolean(assignCourse && !assignCourse.isMaster);
  const assignerIsGlobal = session?.acuityLearn?.schoolScope?.type === "global";
  const creatorSchool = placingDraft ? creatorSchoolOption(assignCourse, selectableSchools) : null;
  const schoolLocked = Boolean(creatorSchool);
  const assignSchools = schoolLocked && creatorSchool ? [creatorSchool] : selectableSchools;
  const pinExclusive = placingDraft && assignMode === "pin";
  const assignSchoolKeys = assignSchools.map((school) => String(school.id));
  const allAssignSelected =
    assignSchoolKeys.length > 0 && assignSchoolKeys.every((id) => schoolIds.includes(id));
  const toggleAssignSchool = (id: string) => {
    if (schoolLocked) return;
    const key = String(id);
    if (schoolIds.includes(key)) {
      setSchoolIds((prev) => prev.filter((row) => row !== key));
      return;
    }
    if (pinExclusive) {
      setSchoolIds([key]);
      return;
    }
    if (placingDraft && assignMode === "pin") setAssignMode("copy");
    setSchoolIds((prev) => [...prev, key]);
  };
  const toggleAllAssignSchools = () => {
    if (schoolLocked || pinExclusive) return;
    setSchoolIds(allAssignSelected ? [] : assignSchoolKeys);
  };
  const taxonomyRequired = taxonomyOptions.length > 0 && !taxonomyId;
  const catalogMode = pageMode === "catalog";
  const othersMode = pageMode === "others";
  const selectedScope = scopedSchools.find((s) => String(s.id) === String(selectedSchoolId)) ?? null;
  const canSwitchScopes = scopedSchools.length > 1;
  const mineSelected = selectedSchoolId === MY_COURSES_SCOPE_ID;
  const othersSelected = othersMode || selectedSchoolId === OTHERS_SCOPE_ID;
  const catalogSelected =
    (catalogMode || selectedSchoolId === CATALOG_SCOPE_ID) && !mineSelected && !othersSelected;
  const courseGroups = othersSelected
    ? othersAuthorGroups(courses)
    : mineSelected
      ? MINE_SECTIONS.map((section) => ({
          ...section,
          items: courses.filter((course) => mineSectionKey(course) === section.key),
        })).filter((section) => section.items.length)
      : [{ key: "all", title: null as string | null, subtitle: null as string | null, items: courses }];
  const permBusy = Boolean(
    permissionsContext && busyAssignment === permissionsContext.assignment.id,
  );
  const permDirty = Boolean(
    permissionsContext &&
      (assignmentAccess(permissionsContext.assignment) !== access ||
        assignmentCopy(permissionsContext.assignment) !== copyAccess ||
        (permissionsContext.kind !== "catalog" &&
          assignmentEnroll(permissionsContext.assignment) !== enrollAccess) ||
        Boolean(permissionsContext.assignment.modLock) !== permModLock),
  );
  const catalogPerm = permissionsContext?.kind === "catalog";
  const catalogLocksFrozen = Boolean(
    catalogPerm && permissionsContext.assignment.modLock && !canEditDespiteModLock,
  );

  const scopeIcon = mineSelected ? (
    <UserRound size={14} aria-hidden="true" />
  ) : othersSelected ? (
    <Users size={14} aria-hidden="true" />
  ) : catalogSelected ? (
    <BookOpen size={14} aria-hidden="true" />
  ) : (
    <School size={14} aria-hidden="true" />
  );

  const createCourseRow = showCreateGhost ? (
    <div
      ref={createSlotRef}
      className="al-courses-ghost-slot"
      style={pinCreate ? { height: pinCreate.height } : undefined}
    >
      <button
        type="button"
        className={`al-courses-ghost-row${pinCreate ? " is-float" : ""}`}
        style={floatRowStyle(pinCreate)}
        disabled={saving}
        onClick={openCreate}
      >
        <span className="al-courses-cover al-courses-cover--empty" aria-hidden="true">
          <Plus size={15} />
        </span>
        <span className="al-courses-ghost-row-label">Create a Course</span>
      </button>
    </div>
  ) : null;

  return (
    <div className="al-courses" data-layout={layout} ref={pageRef}>
      <LoadingOverlay isVisible={loading} message="Loading courses…" />
      <header className="al-courses-header">
        <div className="al-courses-header-text">
          <div className="al-courses-title-row">
            <h1>Courses</h1>
            <button
              type="button"
              className="al-courses-info"
              aria-label="How courses work"
              title="How courses work"
              onClick={() => setShowCourseHelp(true)}
            >
              <CircleHelp size={15} aria-hidden="true" />
            </button>
          </div>
          <p className="al-courses-sub">
            {mineSelected
              ? "Courses you created, grouped by whether they sit unused, live in the catalog, or already went to a school."
              : othersSelected
                ? "Unowned courses other people in your scope wrote. Not on a school yet."
                : catalogSelected
                  ? "Catalog courses can be given to a school. The school gets its own copy."
                  : selectedScope
                    ? `Courses ${selectedScope.name} has been given.`
                    : "Courses your school has been given."}
          </p>
        </div>
        <div className="al-courses-header-actions">
          {selectedScope ? (
            <div className="al-courses-school-switch" ref={schoolMenuRef}>
              {canSwitchScopes ? (
                <>
                  <button
                    type="button"
                    className="al-courses-school-badge"
                    aria-haspopup="listbox"
                    aria-expanded={schoolMenuOpen}
                    aria-label={SCOPE_SWITCH_LABEL}
                    onClick={() => setSchoolMenuOpen((open) => !open)}
                  >
                    {scopeIcon}
                    {selectedScope.name}
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  {schoolMenuOpen ? (
                    <ul
                      className="al-courses-school-menu"
                      role="listbox"
                      aria-label={SCOPE_SWITCH_LABEL}
                    >
                      {scopedSchools.map((school) => (
                        <li key={school.id} role="option" aria-selected={school.id === selectedSchoolId}>
                          <button
                            type="button"
                            className={
                              school.id === selectedSchoolId
                                ? "al-courses-school-option al-courses-school-option--active"
                                : "al-courses-school-option"
                            }
                            onClick={() => pickScope(school.id)}
                          >
                            {school.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <span className="al-courses-school-badge al-courses-school-badge--static">
                  {scopeIcon}
                  {selectedScope.name}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </header>

      {selectedSchoolId &&
      selectedSchoolId !== MY_COURSES_SCOPE_ID &&
      selectedSchoolId !== OTHERS_SCOPE_ID &&
      selectedSchoolId !== CATALOG_SCOPE_ID ? (
        <SchoolProposalList schoolId={selectedSchoolId} />
      ) : null}

      {!loading && courses.length === 0 ? (
        showCreateGhost ? (
          <div className="al-courses-grid al-courses-grid--list" ref={courseListRef}>
            {createCourseRow}
          </div>
        ) : (
        <div className="al-courses-empty">
          {othersSelected ? <Users size={28} /> : <BookOpen size={28} />}
          <h2>{othersSelected ? "No one else's courses" : "No courses yet"}</h2>
          <p>
            {mineSelected
              ? "Courses you create land here. They stay listed after they go to the catalog or a school."
              : othersSelected
                ? "Nobody else in your scope has a course sitting off a school."
                : catalogMode
                  ? canCreate
                    ? "Create a Course to get started. Add it to the catalog if other schools should be able to receive a copy."
                    : "Run `npm run setup-db` to seed the starter courses."
                  : selectedScope
                    ? `${selectedScope.name} has not been given any courses yet.`
                    : "No schools in your scope have courses yet."}
          </p>
        </div>
        )
      ) : (
        <div className="al-courses-groups" ref={courseListRef}>
          {courseGroups.map((group, groupIndex) => {
            const MineIcon = mineSelected ? MINE_SECTION_ICONS[group.key as MineSectionKey] : undefined;
            return (
            <section
              key={group.key}
              id={mineSelected ? `al-mine-${group.key}` : undefined}
              className="al-courses-group"
            >
              {group.title ? (
                <header className="al-courses-group-head">
                  <h2 className="al-courses-group-title">
                    {MineIcon ? (
                      <span className="al-courses-group-icon" aria-hidden="true">
                        <MineIcon size={14} />
                      </span>
                    ) : null}
                    {group.title}
                  </h2>
                  {group.subtitle ? <p className="al-courses-group-sub">{group.subtitle}</p> : null}
                </header>
              ) : null}
              <div className={layout === "list" ? "al-courses-grid al-courses-grid--list" : "al-courses-grid"}>
          {group.items.map((course) => {
            const placement = coursePlacement(course);
            const onSchoolCopy = mineSelected && placement === "school";
            const total = course.assignments.length;
            const copiedOpen = Boolean(expandedCourseIds[course.id]);
            const copyWithdrawn = Boolean(course.assignments[0]?.withdrawn);
            const copyCanEdit = Boolean(course.assignments[0]?.canEdit);
            const copyCanPropose = Boolean(course.assignments[0]?.canPropose);
            const copyCanCopy = course.assignments[0]?.canCopy !== false;
            const copyWritable = copyCanEdit || copyCanPropose;
            const onCatalogMaster = catalogMode && !onSchoolCopy && Boolean(course.isMaster);
            const unattachedDraft = !course.ownerSchoolId && !Boolean(course.isMaster);
            const ownShelf = Boolean(mineSelected && unattachedDraft);
            const masterLive = !Boolean(course.modLock) && course.canEdit !== false;
            const masterProposeOnly =
              !Boolean(course.modLock) && course.canEdit === false && course.canPropose === true;
            const masterWritable = Boolean(
              (canEditMaster && masterLive) ||
                (canProposeMaster && masterProposeOnly) ||
                canEditDespiteModLock,
            );
            const showEdit =
              layout === "list"
                ? false
                : othersSelected
                  ? canEdit
                  : ownShelf
                    ? Boolean(canCreateRegular || canEdit)
                    : catalogMode
                      ? onSchoolCopy
                        ? canEdit && copyWritable
                        : masterWritable
                      : canEdit && copyWritable;
            const showBuild =
              canOpenBuilder &&
              Boolean(
                onCatalogMaster
                  ? course.canBuild
                  : course.canBuild || course.assignments[0]?.canBuild,
              );
            const showListGear =
              layout === "list" &&
              catalogMode &&
              !onSchoolCopy &&
              canSetMasterAccess &&
              Boolean(course.isMaster);
            const showDuplicate =
              !othersSelected &&
              (!catalogMode || onSchoolCopy) &&
              canCopyCourses &&
              copyCanCopy &&
              !copyWithdrawn;
            const copyId = catalogMode && !onSchoolCopy ? null : course.assignments[0]?.courseId || course.id;
            const showAnnounce =
              Boolean(
                canPostAnnouncements &&
                  !course.isMaster &&
                  course.ownerSchoolId &&
                  !copyWithdrawn &&
                  (copyId || course.id)
              );
            const canGiveOut = !othersSelected && canAssign && Boolean(course.isMaster) && !onSchoolCopy;
            const canPlaceThis =
              (mineSelected || othersSelected) &&
              canPlaceOnSchool &&
              !Boolean(course.isMaster) &&
              !course.ownerSchoolId;
            const canPromoteToCatalog =
              mineSelected &&
              canCreateMaster &&
              !course.ownerSchoolId &&
              !Boolean(course.isMaster) &&
              !course.catalogUpgrade;
            const canRequestCatalog = canRequestCatalogFor(course);
            const hasCardActions =
              showEdit ||
              showBuild ||
              showDuplicate ||
              showAnnounce ||
              showListGear;

            return (
              <div
                key={course.id}
                className="al-courses-list-block"
                data-course-id={course.id}
              >
              <article className={`al-courses-card${hasCardActions ? " al-courses-card--actions" : ""}`}>
                {course.coverImageUrl && !brokenCovers[course.id] ? (
                  <img
                    className="al-courses-cover"
                    src={course.coverImageUrl}
                    alt=""
                    loading="lazy"
                    onError={() => setBrokenCovers((prev) => ({ ...prev, [course.id]: true }))}
                  />
                ) : (
                  <div className="al-courses-cover al-courses-cover--empty">
                    <BookOpen size={24} />
                  </div>
                )}

                <div className="al-courses-card-body">
                  <div className="al-courses-card-meta">
                    <div className="al-courses-card-title-row">
                      <h2 className="al-courses-card-title" title={course.description || undefined}>
                        {course.title}
                      </h2>
                      {mineSelected &&
                      (mineSectionKey(course) === "assigned" || mineSectionKey(course) === "school") ? (
                        <span className="al-courses-badge">
                          {course.assignments[0]?.schoolName ||
                            (mineSectionKey(course) === "assigned" ? "Assigned" : "On a school")}
                        </span>
                      ) : null}
                    </div>
                    {course.primaryTaxonomy?.title ? (
                      <p className="al-courses-tax">
                        {taxonomyKindLabel(course.primaryTaxonomy.type)} · {course.primaryTaxonomy.title}
                      </p>
                    ) : null}
                    {course.description ? (
                      <p className="al-courses-card-desc">{course.description}</p>
                    ) : null}
                  </div>

                  {unattachedDraft &&
                  (canPlaceThis || canPromoteToCatalog || canRequestCatalog || course.catalogUpgrade) ? (
                    <div className="al-courses-assignments">
                      <div className="al-courses-shelf-actions">
                        {canPlaceThis ? (
                          <button
                            type="button"
                            className="al-courses-place"
                            onClick={() => openAssign(course.id)}
                          >
                            <School size={14} aria-hidden="true" />
                            Put on a school
                          </button>
                        ) : null}
                        {canPromoteToCatalog ? (
                          <button
                            type="button"
                            className="al-courses-place"
                            title="Add this course to the catalog"
                            aria-label={`Add ${course.title} to the catalog`}
                            disabled={busyAssignment === course.id}
                            onClick={() => openMasterTarget(course)}
                          >
                            <BookOpen size={14} aria-hidden="true" />
                            Add to catalog
                          </button>
                        ) : null}
                        <CatalogRequestText
                          pending={course.catalogUpgrade}
                          busy={
                            busyAssignment === course.id ||
                            busyAssignment === course.catalogUpgrade?.id
                          }
                          onRequest={canRequestCatalog ? () => openMasterTarget(course) : undefined}
                          onWithdraw={
                            course.catalogUpgrade
                              ? () => void handleWithdrawUpgrade(course.catalogUpgrade!.id)
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  ) : othersSelected ? null : catalogMode && !onSchoolCopy ? (
                  <div className="al-courses-assignments">
                    <div className="al-courses-assignments-head">
                      {total === 0 ? (
                        <p className="al-courses-assignments-title">No schools</p>
                      ) : (
                        <button
                          type="button"
                          className="al-courses-copied"
                          aria-expanded={copiedOpen}
                          onClick={() => toggleCopiedSchools(course.id)}
                        >
                          <ChevronDown size={14} aria-hidden="true" />
                          Copied to {total} {total === 1 ? "course" : "courses"}
                        </button>
                      )}
                      <span className="al-courses-school-actions">
                        {course.isMaster && layout !== "list" ? (
                          <AssignmentFlags
                            assignment={staffAssignment(course)}
                            courseTitle={course.title}
                            showOpenCopy={false}
                            showEnroll={false}
                            busy={busyAssignment === course.id}
                            onPermissions={
                              canSetMasterAccess
                                ? () => openPermissions(staffAssignment(course))
                                : undefined
                            }
                          />
                        ) : null}
                        {canGiveOut ? (
                          <button
                            type="button"
                            className="al-courses-add"
                            onClick={() => openAssign(course.id)}
                            title="Give this course to a school"
                            aria-label={`Give ${course.title} to a school`}
                          >
                            <Plus size={14} aria-hidden="true" />
                          </button>
                        ) : null}
                      </span>
                    </div>

                    {copiedOpen && total > 0 && layout !== "list" ? (
                      <ul className="al-courses-school-list">
                        {course.assignments.map((assignment) => (
                          <li key={assignment.id} className="al-courses-school">
                            <span className="al-courses-school-name" title={assignment.schoolName}>
                              {assignment.schoolName}
                            </span>
                            <span className="al-courses-school-actions">
                              <AssignmentFlags
                                assignment={assignment}
                                courseTitle={course.title}
                                showOpenCopy={Boolean(canOpenBuilder && assignment.canBuild)}
                                showEnroll={canManageEnrollments}
                                busy={busyAssignment === assignment.id}
                                onPermissions={
                                  canSetCopyAccess || canAssign
                                    ? () => openPermissions(assignment)
                                    : undefined
                                }
                                onEnroll={
                                  canManageEnrollments
                                    ? () =>
                                        setEnrollTarget({
                                          courseId: assignment.courseId,
                                          title: course.title,
                                          schoolId: assignment.schoolId,
                                          schoolName: assignment.schoolName,
                                        })
                                    : undefined
                                }
                              />
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  ) : (
                    <div className="al-courses-assignments">
                      {course.assignments[0] ? (
                        <AssignmentFlags
                          assignment={course.assignments[0]}
                          courseTitle={course.title}
                          showEnroll={canManageEnrollments}
                          busy={busyAssignment === copyId}
                          onPermissions={
                            canSetCopyAccess || canAssign
                              ? () => openPermissions(course.assignments[0])
                              : undefined
                          }
                          onEnroll={
                            canManageEnrollments && copyId
                              ? () =>
                                  setEnrollTarget({
                                    courseId: copyId,
                                    title: course.title,
                                    schoolId: course.assignments[0].schoolId,
                                    schoolName: course.assignments[0].schoolName,
                                  })
                              : undefined
                          }
                        />
                      ) : null}
                      {((canRequestCatalog && !canSetCopyAccess && !canAssign) || course.catalogUpgrade) ? (
                        <div className="al-courses-shelf-actions">
                          <CatalogRequestText
                            pending={course.catalogUpgrade}
                            busy={
                              busyAssignment === course.id ||
                              busyAssignment === course.catalogUpgrade?.id
                            }
                            onRequest={
                              canRequestCatalog && !canSetCopyAccess && !canAssign
                                ? () => openMasterTarget(course)
                                : undefined
                            }
                            onWithdraw={
                              course.catalogUpgrade
                                ? () => void handleWithdrawUpgrade(course.catalogUpgrade!.id)
                                : undefined
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                {hasCardActions ? (
                  <div className="al-courses-card-tools">
                    {showEdit ? (
                      <button
                        type="button"
                        className="al-courses-edit"
                        onClick={() => openEdit(course)}
                        title="Edit title, description, or category"
                        aria-label={`Edit ${course.title}`}
                      >
                        <Pencil size={13} aria-hidden="true" />
                        <span className="al-courses-tool-label">Edit</span>
                      </button>
                    ) : null}
                    {showBuild ? (
                      <Link
                        to={`/courses/${encodeURIComponent(copyId || course.id)}/build`}
                        className="al-courses-build"
                        title={
                          othersSelected || catalogMode
                            ? "Open this course in the course builder"
                            : "Open this school’s course copy"
                        }
                        aria-label={`Build ${course.title}`}
                      >
                        <Hammer size={13} aria-hidden="true" />
                        <span className="al-courses-tool-label">Build</span>
                      </Link>
                    ) : null}
                    {showListGear ? (
                      <button
                        type="button"
                        className="al-courses-settings"
                        title="Change catalog access"
                        aria-label={`Change catalog access for ${course.title}`}
                        disabled={busyAssignment === course.id}
                        onClick={() => openPermissions(staffAssignment(course))}
                      >
                        <Settings size={13} aria-hidden="true" />
                        <span className="al-courses-tool-label">Access</span>
                      </button>
                    ) : null}
                    {showAnnounce ? (
                      <button
                        type="button"
                        className="al-courses-announce"
                        title="Post a note students will see on Today"
                        aria-label={`Announce in ${course.title}`}
                        onClick={() =>
                          setAnnounceTarget({
                            courseId: copyId || course.id,
                            title: course.title,
                          })
                        }
                      >
                        <Megaphone size={13} aria-hidden="true" />
                        <span className="al-courses-tool-label">Announce</span>
                      </button>
                    ) : null}
                    {showDuplicate ? (
                      <button
                        type="button"
                        className="al-courses-duplicate"
                        title="Copy this course for this school"
                        aria-label={`Copy ${course.title}`}
                        disabled={busyAssignment === copyId}
                        onClick={() => void handleCopyCourse(course)}
                      >
                        <Copy size={13} aria-hidden="true" />
                        <span className="al-courses-tool-label">Copy</span>
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
              {layout === "list" && copiedOpen && total > 0 ? (
                <ul
                  className="al-courses-copy-rows"
                  data-course-copies={course.id}
                  aria-label={`School copies of ${course.title}`}
                >
                  {course.assignments.map((assignment) => (
                    <li key={assignment.id} className="al-courses-copy-row">
                      <span className="al-courses-copy-gutter" aria-hidden="true" />
                      <span className="al-courses-copy-label" title={assignment.schoolName}>
                        <span className="al-courses-copy-kind">Copy</span>
                        {assignment.schoolName}
                      </span>
                      <span className="al-courses-card-tools">
                        {canOpenBuilder && assignment.canBuild ? (
                          <Link
                            to={`/courses/${encodeURIComponent(assignment.courseId)}/build`}
                            className="al-courses-build"
                            title={`Open ${assignment.schoolName}’s copy`}
                            aria-label={`Build ${course.title} for ${assignment.schoolName}`}
                          >
                            <Hammer size={13} aria-hidden="true" />
                            <span className="al-courses-tool-label">Build</span>
                          </Link>
                        ) : null}
                        {canManageEnrollments ? (
                          <button
                            type="button"
                            className="al-courses-enroll"
                            title={`Enroll people at ${assignment.schoolName}`}
                            aria-label={`Enroll people in ${course.title} at ${assignment.schoolName}`}
                            disabled={busyAssignment === assignment.id}
                            onClick={() =>
                              setEnrollTarget({
                                courseId: assignment.courseId,
                                title: course.title,
                                schoolId: assignment.schoolId,
                                schoolName: assignment.schoolName,
                              })
                            }
                          >
                            <UserPlus size={13} aria-hidden="true" />
                            <span className="al-courses-tool-label">Enroll</span>
                          </button>
                        ) : null}
                        {canSetCopyAccess || canAssign ? (
                          <button
                            type="button"
                            className="al-courses-settings"
                            title={`Change access for ${assignment.schoolName}`}
                            aria-label={`Change access for ${assignment.schoolName}`}
                            disabled={busyAssignment === assignment.id}
                            onClick={() => openPermissions(assignment)}
                          >
                            <Settings size={13} aria-hidden="true" />
                            <span className="al-courses-tool-label">Access</span>
                          </button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              </div>
            );
          })}
              {groupIndex === courseGroups.length - 1 ? createCourseRow : null}
              </div>
            </section>
            );
          })}
        </div>
      )}

      {/* Give this course to a school / place a draft */}
      <NotificationModal
        isOpen={Boolean(assignCourse)}
        type="neutral"
        icon={<School />}
        title={
          selectableSchools.length === 0
            ? placingDraft
              ? "No school in your scope"
              : "Every school already has this course"
            : placingDraft
              ? "Put on a school"
              : "Give course to a school"
        }
        message={
          selectableSchools.length === 0
            ? placingDraft
              ? assignerIsGlobal
                ? "This course is not on a school. Pick a school in your scope, then attach it."
                : "This course is not on a school. Switch context, or ask for a school in scope."
              : "Every school already has this course. Therefore, you cannot give it to any school."
            : placingDraft
              ? schoolLocked
                ? `${assignCourse?.title || "This course"} is not on a school yet. It will be put on ${creatorSchool?.name}.`
                : assignerIsGlobal
                  ? `${assignCourse?.title || "This course"} is not on a school yet. Choose which school it should belong to.`
                  : `${assignCourse?.title || "This course"} is not on a school yet.`
              : assignCourse?.title
        }
        overlay
        overlayClose={!saving}
        showOkButton={false}
        okButtonText="OK"
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={SCHOOL_ACCESS_DIALOG_STYLE}
        onClose={closeAssign}
      >
        {selectableSchools.length === 0 ? null : (
          <form className="al-courses-dialog-form al-courses-dialog-form--access" onSubmit={(event) => void handleAssign(event)}>
            <div className="al-courses-field">
              <div className="al-courses-field-head">
                <div>
                  <span className="al-courses-label" id="assign-school-label">
                    School
                  </span>
                  <p className="al-courses-label-hint" id="assign-school-hint">
                    {allAssignSelected && assignSchools.length > 1
                      ? `All ${assignSchools.length} selected`
                      : schoolIds.length > 1
                        ? `${schoolIds.length} selected`
                        : "Select a school"}
                  </p>
                </div>
                {!pinExclusive && !schoolLocked && assignSchools.length > 1 ? (
                  <button
                    type="button"
                    className="al-courses-school-toggle"
                    disabled={saving}
                    onClick={toggleAllAssignSchools}
                  >
                    {allAssignSelected ? "Deselect all" : "Select all"}
                  </button>
                ) : null}
              </div>
              <div
                className="al-courses-school-picks"
                role="group"
                aria-labelledby="assign-school-label"
                aria-describedby="assign-school-hint"
              >
                {assignSchools.map((school) => {
                  const selected = schoolIds.includes(String(school.id));
                  return (
                    <label
                      key={school.id}
                      className={`al-courses-school-pick${selected ? " is-on" : ""}`}
                    >
                      <input
                        type={pinExclusive ? "radio" : "checkbox"}
                        name="assign-school"
                        checked={selected}
                        disabled={saving || schoolLocked}
                        onChange={() => toggleAssignSchool(String(school.id))}
                      />
                      <span>{school.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {placingDraft ? (
              <div className="al-courses-access-cards">
                <button
                  type="button"
                  className={`al-courses-access-card${assignMode === "pin" ? " al-courses-access-card--active" : ""}`}
                  disabled={saving || schoolIds.length > 1}
                  onClick={() => {
                    setAssignMode("pin");
                    setSchoolIds((prev) => prev.slice(0, 1));
                  }}
                >
                  <span className="al-courses-access-card-title">Use this course on the school</span>
                  <span className="al-courses-access-card-hint">
                    {schoolIds.length > 1
                      ? "Pick one school to attach this course as-is. Extra schools need a copy."
                      : "Recommended. This course becomes the school’s. No extra copy."}
                  </span>
                </button>
                <button
                  type="button"
                  className={`al-courses-access-card${assignMode === "copy" ? " al-courses-access-card--active" : ""}`}
                  disabled={saving}
                  onClick={() => setAssignMode("copy")}
                >
                  <span className="al-courses-access-card-title">Leave this course off the school, give a copy</span>
                  <span className="al-courses-access-card-hint">
                    The original stays unattached. Each selected school gets its own row pointed at it.
                  </span>
                </button>
              </div>
            ) : null}

            <div className="al-courses-assign-defaults">
              <p className="al-courses-assign-caption">
                {schoolIds.length > 1
                  ? "Below are the settings for what each selected school and its members can do with this course."
                  : "Below are the settings for what this school and its members can do with this course."}
              </p>
              {!canSetCopyAccess ? (
                <p className="al-courses-access-caption">
                  Access and Copy are the defaults for a copy given without Set School Copy Access
                  (Propose, cannot copy). Enrollment can still be changed here.
                </p>
              ) : null}
              <CourseAccessFields
                accessName="assign-access"
                copyName="assign-copy"
                access={access}
                copyAccess={copyAccess}
                onAccess={setAccess}
                onCopy={setCopyAccess}
                disabled={saving}
                locksDisabled={!canSetCopyAccess}
                showLocks
                enrollName="assign-enroll"
                enrollAccess={enrollAccess}
                onEnroll={setEnrollAccess}
              />
            </div>

            <div className="al-courses-assign-actions">
              <button
                type="button"
                className="al-courses-btn al-courses-btn--ghost"
                onClick={closeAssign}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="al-courses-btn" disabled={saving || !schoolIds.length}>
                {saving
                  ? placingDraft && assignMode === "pin"
                    ? "Placing…"
                    : "Giving…"
                  : placingDraft && assignMode === "pin"
                    ? "Put on school"
                    : schoolIds.length > 1
                      ? `Give to ${schoolIds.length} schools`
                      : "Give course"}
              </button>
            </div>
          </form>
        )}
      </NotificationModal>

      {/* Change access or withdraw a school assignment */}
      <NotificationModal
        isOpen={Boolean(permissionsContext)}
        type="neutral"
        icon={<Shield />}
        title={catalogPerm ? "Catalog access" : "School access"}
        message={
          permissionsContext
            ? catalogPerm
              ? permissionsContext.course.title
              : `${permissionsContext.assignment.schoolName} — ${permissionsContext.course.title}`
            : null
        }
        overlay
        overlayClose={!busyAssignment && !withdrawTarget && !masterTarget}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={SCHOOL_ACCESS_DIALOG_STYLE}
        onClose={closePermissions}
      >
        {permissionsContext && (catalogPerm ? canSetMasterAccess : canSetCopyAccess || canAssign) ? (
          <form
            className="al-courses-dialog-form al-courses-dialog-form--access"
            onSubmit={(event) => void handleSavePermissions(event)}
          >
            {permissionsContext.assignment.withdrawn ? (
              <p className="al-courses-withdrawn-hint">
                This copy is withdrawn from the school catalog. Restore it to list it again.
                People already enrolled keep access. Access and enrollment settings below stay as they were.
              </p>
            ) : null}
            {catalogLocksFrozen ? (
              <p className="al-courses-withdrawn-hint">
                This template is mod-locked. Only Edit Despite Mod Lock can change access.
              </p>
            ) : null}
            {catalogPerm ? (
              <p className="al-courses-access-caption">
                These settings apply to other catalog editors in Course Builder, and to copying this
                template. Later changes need Update Catalog Access.
              </p>
            ) : !canSetCopyAccess && canAssign ? (
              <p className="al-courses-access-caption">
                Access, Copy, and Mod lock are shown as this copy’s settings (the defaults without Set
                School Copy Access). They cannot be changed here. Enrollment on/off and
                remove-from-school still use Assign Courses To Schools.
              </p>
            ) : (
              <p className="al-courses-access-caption">
                Access, Copy, and Mod lock apply to writers on this school’s copy (Edit Courses, or
                Teach Enrolled plus an instructor enrollment). Enrollment on/off is Assign Courses
                To Schools.
              </p>
            )}
            <CourseAccessFields
              accessName="perm-access"
              copyName="perm-copy"
              access={access}
              copyAccess={copyAccess}
              onAccess={setAccess}
              onCopy={setCopyAccess}
              disabled={permBusy || catalogLocksFrozen}
              locksDisabled={!catalogPerm && !canSetCopyAccess}
              audience={catalogPerm ? "catalog" : "school"}
              showLocks={catalogPerm ? canSetMasterAccess : canSetCopyAccess || canAssign}
              lockName={
                catalogPerm
                  ? canSetMasterAccess
                    ? "perm-lock"
                    : undefined
                  : canSetCopyAccess || canAssign
                    ? "perm-lock"
                    : undefined
              }
              modLock={permModLock}
              onModLock={
                catalogPerm
                  ? canSetMasterAccess
                    ? setPermModLock
                    : undefined
                  : canSetCopyAccess || canAssign
                    ? setPermModLock
                    : undefined
              }
              enrollName={catalogPerm ? undefined : "perm-enroll"}
              enrollAccess={catalogPerm ? undefined : enrollAccess}
              onEnroll={catalogPerm ? undefined : setEnrollAccess}
            />
            {catalogPerm && canCreateMaster ? (
              <button
                type="button"
                className="al-courses-catalog-remove"
                disabled={permBusy}
                onClick={() => openMasterTarget(permissionsContext.course)}
              >
                Remove from catalog
              </button>
            ) : null}
            {!catalogPerm && (canRequestCatalogFor(permissionsContext.course) || permissionsContext.course.catalogUpgrade) ? (
              <CatalogRequestText
                pending={permissionsContext.course.catalogUpgrade}
                busy={
                  permBusy ||
                  busyAssignment === permissionsContext.course.id ||
                  busyAssignment === permissionsContext.course.catalogUpgrade?.id
                }
                onRequest={
                  canRequestCatalogFor(permissionsContext.course)
                    ? () => {
                        const course = permissionsContext.course;
                        closePermissions();
                        openMasterTarget(course);
                      }
                    : undefined
                }
                onWithdraw={
                  permissionsContext.course.catalogUpgrade
                    ? () => void handleWithdrawUpgrade(permissionsContext.course.catalogUpgrade!.id)
                    : undefined
                }
              />
            ) : null}
            <div className="al-courses-assign-actions">
              {!catalogPerm && canAssign && permissionsContext.assignment.withdrawn ? (
                <button
                  type="button"
                  className="al-courses-btn al-courses-btn--ghost"
                  disabled={permBusy}
                  onClick={() => void handleRestore(permissionsContext.assignment)}
                >
                  Restore to school
                </button>
              ) : null}
              {!catalogPerm && canAssign && !permissionsContext.assignment.withdrawn ? (
                <button
                  type="button"
                  className="al-courses-btn al-courses-btn--ghost al-courses-btn--danger"
                  disabled={permBusy}
                  onClick={() =>
                    setWithdrawTarget({
                      course: permissionsContext.course,
                      assignment: permissionsContext.assignment,
                    })
                  }
                >
                  Remove from school
                </button>
              ) : null}
              <button
                type="button"
                className="al-courses-btn al-courses-btn--ghost"
                onClick={closePermissions}
                disabled={permBusy}
              >
                Cancel
              </button>
              {(catalogPerm ? canSetMasterAccess : canSetCopyAccess || canAssign) ? (
                <button
                  type="submit"
                  className="al-courses-btn"
                  disabled={permBusy || !permDirty || catalogLocksFrozen}
                >
                  {permBusy ? "Saving…" : "Save"}
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(withdrawTarget)}
        type="warning"
        icon={<School />}
        title="Withdraw from school?"
        message={
          withdrawTarget
            ? `Take “${withdrawTarget.course.title}” back from ${withdrawTarget.assignment.schoolName}? The copy will be hidden from that school’s catalog. People already enrolled keep access until they are revoked. The original template is not affected.`
            : null
        }
        overlay
        overlayClose={!busyAssignment}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1200}
        onClose={() => {
          if (!busyAssignment) setWithdrawTarget(null);
        }}
      >
        <div className="al-courses-assign-actions">
          <button
            type="button"
            className="al-courses-btn al-courses-btn--ghost"
            onClick={() => setWithdrawTarget(null)}
            disabled={Boolean(busyAssignment)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="al-courses-btn al-courses-btn--danger-solid"
            onClick={() => void handleRevoke()}
            disabled={Boolean(busyAssignment)}
          >
            {busyAssignment && withdrawTarget && busyAssignment === withdrawTarget.assignment.id
              ? "Withdrawing…"
              : "Withdraw"}
          </button>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(masterTarget)}
        type="warning"
        icon={masterTarget?.isMaster ? <BookMarked /> : <BookOpen />}
        title={
          masterTarget?.isMaster
            ? "Move to shelf?"
            : canCreateMaster
              ? "Add to catalog?"
              : "Request catalog?"
        }
        message={
          masterTarget?.isMaster
            ? `Take “${masterTarget.title}” out of the catalog? Existing school copies stay as they are. The course returns to My shelf.`
            : masterTarget
              ? canCreateMaster
                ? `Move “${masterTarget.title}” into the catalog? Schools can then receive a copy. Access / Copy / Mod lock below are for other catalog editors after this is a template — not school access. Later changes need Update Catalog Access.`
                : masterTarget.ownerSchoolId
                  ? `Ask a catalog admin to add “${masterTarget.title}” to the catalog. This school’s copy stays here; applying the request creates a new catalog template from it.`
                  : `Ask a catalog admin to add “${masterTarget.title}” to the catalog.`
              : null
        }
        overlay
        overlayClose={!busyAssignment}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1200}
        onClose={() => {
          if (!busyAssignment) setMasterTarget(null);
        }}
      >
        {masterTarget && !masterTarget.isMaster && canCreateMaster ? (
          <CourseAccessFields
            accessName="master-access"
            copyName="master-copy"
            access={masterAccess}
            copyAccess={masterCopy}
            onAccess={setMasterAccess}
            onCopy={setMasterCopy}
            audience="catalog"
            lockName="master-lock"
            modLock={masterModLock}
            onModLock={setMasterModLock}
            disabled={Boolean(busyAssignment)}
          />
        ) : null}
        <div className="al-courses-assign-actions">
          <button
            type="button"
            className="al-courses-btn al-courses-btn--ghost"
            onClick={() => setMasterTarget(null)}
            disabled={Boolean(busyAssignment)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="al-courses-btn"
            onClick={() => void handleSetMaster()}
            disabled={Boolean(busyAssignment)}
          >
            {busyAssignment && masterTarget && busyAssignment === masterTarget.id
              ? masterTarget.isMaster
                ? "Moving…"
                : canCreateMaster
                  ? "Adding…"
                  : "Requesting…"
              : masterTarget?.isMaster
                ? "Move to shelf"
                : canCreateMaster
                  ? "Add to catalog"
                  : "Request catalog"}
          </button>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={showCreate}
        type="neutral"
        icon={<BookOpen />}
        title="Create a Course"
        message={null}
        overlay
        overlayClose={!saving && !taxonomyPickerOpen && !showMasterInfo}
        escapeClose={!taxonomyPickerOpen && !showMasterInfo}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={COURSE_DIALOG_STYLE}
        onClose={closeCreate}
      >
        {canCreateMaster ? (
          <div className="al-courses-master-row">
            <button
              type="button"
              className={
                asMaster
                  ? "al-courses-badge al-courses-badge--master al-courses-badge--clickable"
                  : "al-courses-badge al-courses-badge--clickable"
              }
              title="Catalog or My courses?"
              aria-haspopup="dialog"
              aria-expanded={showMasterInfo}
              disabled={saving}
              onClick={() => setShowMasterInfo(true)}
            >
              {asMaster ? "In catalog" : "My courses only"}
            </button>
          </div>
        ) : null}

        <form className="al-courses-dialog-form" onSubmit={(event) => void handleCreate(event)}>
          <CourseMetaFields
            title={newTitle}
            description={newDescription}
            taxonomyId={taxonomyId}
            taxonomyOptions={taxonomyOptions}
            taxonomyLoading={taxonomyLoading}
            saving={saving}
            onTitle={setNewTitle}
            onDescription={setNewDescription}
            onTaxonomy={setTaxonomyId}
            onTaxonomyOptions={setTaxonomyOptions}
            onPickerOpenChange={setTaxonomyPickerOpen}
          />
          {canCreateMaster && asMaster ? (
            <CourseAccessFields
              accessName="create-access"
              copyName="create-copy"
              access={createAccess}
              copyAccess={createCopy}
              onAccess={setCreateAccess}
              onCopy={setCreateCopy}
              audience="catalog"
              lockName="create-lock"
              modLock={createModLock}
              onModLock={setCreateModLock}
              disabled={saving}
            />
          ) : null}

          <div className="al-courses-assign-actions">
            <button
              type="button"
              className="al-courses-btn al-courses-btn--ghost"
              onClick={closeCreate}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="al-courses-btn"
              disabled={saving || !newTitle.trim() || taxonomyRequired}
            >
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </NotificationModal>

      <NotificationModal
        isOpen={showMasterInfo}
        type="neutral"
        icon={<BookMarked />}
        title="Catalog or My courses?"
        message="Pick where this course should live. Create Master Courses can add it to the catalog now or later. Propose to Catalog only requests an upgrade."
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1250}
        style={COURSE_DIALOG_STYLE}
        onClose={() => setShowMasterInfo(false)}
      >
        <div className="al-courses-access-cards">
          <button
            type="button"
            className={`al-courses-access-card${asMaster ? " al-courses-access-card--active" : ""}`}
            onClick={() => {
              setAsMaster(true);
              setShowMasterInfo(false);
            }}
          >
            <span className="al-courses-access-card-title">Catalog</span>
            <span className="al-courses-access-card-hint">
              Other schools can receive a copy. Your original stays as it is. Only catalog courses
              can be given to a school.
            </span>
          </button>
          <button
            type="button"
            className="al-courses-access-card"
            disabled={!canCreateRegular}
            onClick={() => {
              setAsMaster(false);
              setShowMasterInfo(false);
            }}
          >
            <span className="al-courses-access-card-title">My courses only</span>
            <span className="al-courses-access-card-hint">
              Starts on My shelf. You can put it on a school, or add it to the catalog later.
              {canCreateRegular ? "" : " You can only add courses to the catalog."}
            </span>
          </button>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(editCourse)}
        type="neutral"
        icon={<BookOpen />}
        title="Edit course"
        message={null}
        overlay
        overlayClose={!saving && !taxonomyPickerOpen}
        escapeClose={!taxonomyPickerOpen}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={COURSE_DIALOG_STYLE}
        onClose={closeEdit}
      >
        <form className="al-courses-dialog-form" onSubmit={(event) => void handleEdit(event)}>
          <CourseMetaFields
            title={newTitle}
            description={newDescription}
            taxonomyId={taxonomyId}
            taxonomyOptions={taxonomyOptions}
            taxonomyLoading={taxonomyLoading}
            saving={saving}
            titleId="edit-course-title"
            descriptionId="edit-course-desc"
            onTitle={setNewTitle}
            onDescription={setNewDescription}
            onTaxonomy={setTaxonomyId}
            onTaxonomyOptions={setTaxonomyOptions}
            onPickerOpenChange={setTaxonomyPickerOpen}
          />
          <div className="al-courses-assign-actions">
            <button
              type="button"
              className="al-courses-btn al-courses-btn--ghost"
              onClick={closeEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="al-courses-btn"
              disabled={saving || !newTitle.trim() || taxonomyRequired}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </NotificationModal>

      <NotificationModal
        isOpen={showCourseHelp}
        type="info"
        title="How courses work"
        message={null}
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={{ width: "min(24rem, 100%)", maxHeight: "min(90vh, 48rem)" }}
        onClose={() => setShowCourseHelp(false)}
      >
        <dl className="al-courses-help">
          <div>
            <dt>Catalog</dt>
            <dd>
              Platform templates. Give-out always copies. The school gets its own row; the template
              stays in the catalog.
            </dd>
          </div>
          <div>
            <dt>My courses</dt>
            <dd>
              Courses you created, split by where they live now.
              <span className="al-courses-help-sub">
                <strong>My shelf.</strong> Not in the catalog and not on a school yet. Create Master
                Courses adds it to the catalog and sets the first catalog-editor locks. Propose to
                Catalog is a small request — it does not set those locks.
              </span>
              <span className="al-courses-help-sub">
                <strong>Also in catalog.</strong> Templates you created. Giving one to a school
                leaves this row here and adds a school copy under Assigned. Same permission can
                move it back to the shelf.
              </span>
              <span className="al-courses-help-sub">
                <strong>On a school.</strong> You attached this course itself to a school — no extra
                copy. Propose to Catalog asks for a new catalog template cloned from it; the school
                keeps this course.
              </span>
              <span className="al-courses-help-sub">
                <strong>Assigned to schools.</strong> Copies you gave a school. The original stays in
                the catalog or on your shelf. Propose to Catalog here also clones a new template;
                this copy stays on the school.
              </span>
            </dd>
          </div>
          <div>
            <dt>Others&apos; courses</dt>
            <dd>
              Unowned drafts other people in your scope wrote. Not templates, and not on a school
              yet. Names show if you can view people on the roster.
            </dd>
          </div>
          <div>
            <dt>Enroll students</dt>
            <dd>
              Students join a school&apos;s course. Put an unattached course on the school first, or
              give a catalog template so the school has a copy.
            </dd>
          </div>
          <div>
            <dt>Put on a school</dt>
            <dd>
              A course with no school yet can be attached as-is, or copied so an original stays
              unattached. Catalog templates always copy. Global users always choose the school.
            </dd>
          </div>
        </dl>
      </NotificationModal>

      <NotificationModal
        isOpen={showCatalogInbox}
        type="neutral"
        icon={<BookMarked />}
        title="Requests"
        message={catalogUpgrades.length ? null : "No pending catalog requests."}
        overlay
        overlayClose={!busyAssignment}
        showOkButton={false}
        align="center"
        iconSize="1.75rem"
        titleSize="1.05rem"
        style={{ width: "min(32rem, 96vw)", maxHeight: "min(90vh, 48rem)" }}
        onClose={() => {
          if (!busyAssignment) setShowCatalogInbox(false);
        }}
      >
        {catalogUpgrades.length ? (
          <ul className="al-courses-inbox-list">
            {catalogUpgrades.map((row) => (
              <li key={row.id} className="al-courses-inbox-row">
                <div className="al-courses-inbox-copy">
                  <span className="al-courses-inbox-title">{row.courseTitle || "Untitled course"}</span>
                  <span className="al-courses-inbox-meta">{requestedByLine(row)}</span>
                </div>
                <span className="al-courses-inbox-actions">
                  <button
                    type="button"
                    className="al-courses-inbox-action al-courses-inbox-action--decline"
                    disabled={Boolean(busyAssignment)}
                    onClick={() => void handleCatalogInbox(row.id, "reject")}
                  >
                    <Ban size={13} aria-hidden="true" />
                    Decline
                  </button>
                  <button
                    type="button"
                    className="al-courses-inbox-action al-courses-inbox-action--promote"
                    disabled={Boolean(busyAssignment)}
                    onClick={() => void handleCatalogInbox(row.id, "apply")}
                  >
                    <BookMarked size={13} aria-hidden="true" />
                    {busyAssignment === row.id ? "Promoting…" : "Promote"}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </NotificationModal>

      <CourseEnrollDialog target={enrollTarget} onClose={() => setEnrollTarget(null)} />
      <CourseAnnounceDialog target={announceTarget} onClose={() => setAnnounceTarget(null)} />
    </div>
  );
}
